import { fhir, type ConsentStatus, type HeldRecord, type HiType, type PublicConsent } from '@curentia/shared';

/** Record types in words a patient understands. */
export const HI_TYPE_LABELS: Readonly<Record<HiType, string>> = {
  WellnessRecord: 'Readings, such as blood pressure and sugar',
  Prescription: 'Prescriptions',
  OPConsultation: 'Consultation notes',
  DiagnosticReport: 'Lab and scan reports',
  DischargeSummary: 'Discharge summaries',
  ImmunizationRecord: 'Vaccinations',
  HealthDocumentRecord: 'Other health documents',
  Invoice: 'Bills',
};

/** The record types a doctor may ask for. Bills are left out: care does not need them. */
export const ASKABLE_TYPES: readonly HiType[] = [
  'WellnessRecord',
  'Prescription',
  'OPConsultation',
  'DiagnosticReport',
  'DischargeSummary',
  'ImmunizationRecord',
  'HealthDocumentRecord',
];

export const CONSENT_STATUS_LABELS: Readonly<Record<ConsentStatus, string>> = {
  requested: 'Waiting for the patient',
  approved: 'Approved',
  denied: 'Denied',
  revoked: 'Withdrawn',
  expired: 'Expired',
};

/** Days an approval may last, as the doctor's form offers them. */
export const ACCESS_DAYS = [7, 30, 90, 180, 365] as const;

const DATE = { day: 'numeric', month: 'short', year: 'numeric' } as const;

/** 9 Oct 2026, the same on every phone whatever its language setting. */
export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IN', DATE);
}

/** 9 Oct 2026, 3:45 pm */
export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', { ...DATE, hour: 'numeric', minute: '2-digit' });
}

/** Jun 2027, for a YYYY-MM month such as a batch's expiry. */
export function formatMonth(month: string): string {
  const at = new Date(`${month}-01T00:00:00`);
  return Number.isNaN(at.getTime()) ? month : at.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
}

/** The value a datetime-local input wants for a time, in this device's own time zone. */
export function localInputValue(date: Date): string {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

/** One document on a timeline, ready to show. */
export interface TimelineItem {
  id: string;
  hiType: HiType | null;
  date: string;
  title: string;
  lines: string[];
  /** Who wrote it and where it came from, such as "Dr Anand Mishra · from Odisha Care Clinic". */
  byline: string;
}

/** Titles for a timeline. Readings get a plain word rather than ABDM's "Wellness Record". */
const TIMELINE_TITLES: Readonly<Record<HiType, string>> = {
  WellnessRecord: 'Readings',
  Prescription: 'Prescription',
  OPConsultation: 'Consultation',
  DiagnosticReport: 'Lab or scan report',
  DischargeSummary: 'Discharge summary',
  ImmunizationRecord: 'Vaccination',
  HealthDocumentRecord: 'Health document',
  Invoice: 'Bill',
};

/**
 * Documents newest first, each summarised. here names where this device's own
 * records come from: the practice's name, or "you" on a patient's phone.
 * Records the reader cannot parse show their title only.
 */
export function timelineOf(records: readonly HeldRecord[], here: string): TimelineItem[] {
  return records
    .filter((record) => record.kind === 'document')
    .map((record) => {
      let summary: fhir.DocumentSummary | null = null;
      try {
        summary = fhir.summarizeDocument(record.resource as unknown as fhir.Bundle);
      } catch {
        summary = null;
      }
      const from = record.source
        ? record.source.from === 'patient'
          ? 'the patient’s phone'
          : (record.source.practiceName ?? 'another practice')
        : here;
      const byline = !record.source && here === 'you' ? 'Added by you' : `${summary?.author ? `${summary.author} · ` : ''}from ${from}`;
      const hiType = record.hiType ?? summary?.hiType ?? null;
      const authored = summary?.title && hiType && summary.title !== fhir.DOCUMENT_TYPES[hiType].title ? summary.title : null;
      return {
        id: record.id,
        hiType,
        date: record.date ?? summary?.date ?? record.createdAt,
        title: authored ?? (hiType ? TIMELINE_TITLES[hiType] : (summary?.title ?? 'Health record')),
        lines: summary?.lines ?? [],
        byline,
      };
    })
    .sort((a, b) => b.date.localeCompare(a.date));
}

/** One reading with where it came from. */
export interface ReadingPoint {
  id: string;
  reading: fhir.Reading;
  from: string;
}

/** Every reading in these documents, oldest first. */
export function readingsOf(records: readonly HeldRecord[], here: string): ReadingPoint[] {
  const points: ReadingPoint[] = [];
  for (const record of records) {
    if (record.hiType !== 'WellnessRecord') continue;
    let readings: fhir.Reading[];
    try {
      readings = fhir.readWellnessRecord(record.resource as unknown as fhir.Bundle).readings;
    } catch {
      continue;
    }
    const from = record.source ? (record.source.from === 'patient' ? 'the patient’s phone' : (record.source.practiceName ?? 'another practice')) : here;
    readings.forEach((reading, i) => points.push({ id: `${record.id}:${i}`, reading, from }));
  }
  return points.sort((a, b) => a.reading.at.localeCompare(b.reading.at));
}

/** The requesting practice's name, from the consent's own list. */
export function requesterOf(consent: PublicConsent): string {
  return consent.practices.find((p) => p.id === consent.requesterPracticeId)?.name ?? 'A practice';
}

/** The names of the source practices a patient named. */
export function sourcesOf(consent: PublicConsent): string[] {
  const sources = new Set(consent.approval?.sources ?? []);
  return consent.practices.filter((p) => sources.has(p.id)).map((p) => p.name);
}
